// src/pages/Settings.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, Button, Alert, Stack, Avatar, Paper, CircularProgress } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import SettingsSidebar from '../components/settings/SettingsSidebar';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';

export default function Settings() {
  const { user, updateUsername } = useAuth();
  const navigate = useNavigate();

  const [activeSection, setActiveSection] = useState('personal-info');
  const [username, setUsername] = useState(user?.username || '');
  const [status, setStatus] = useState({ ok: false, err: '' });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(username);
  
  // Profile picture states
  const [profilePictureUrl, setProfilePictureUrl] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setUsername(user?.username || '');
    if (!editing) setDraft(user?.username || '');
  }, [user?.username, editing]);

  // Fetch profile picture on mount
  useEffect(() => {
    // Always try to fetch profile picture when Settings page loads
    if (user) {
      fetchProfilePictureUrl();
    }
  }, [user]); // Removed user?.profilePicture dependency

  async function fetchProfilePictureUrl() {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('rv_access_token');
      const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
      
      const response = await fetch(`${API_BASE}/api/profile/picture-url`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProfilePictureUrl(data.url);
      }
    } catch (error) {
      console.error('Failed to fetch profile picture:', error);
    }
  }

  async function handleImageUpload(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setStatus({ ok: false, err: 'Please select an image file' });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setStatus({ ok: false, err: 'Image must be less than 5MB' });
      return;
    }

    setUploadingImage(true);
    setStatus({ ok: false, err: '' });

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result;

        const token = localStorage.getItem('access_token') || localStorage.getItem('rv_access_token');
        const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

        const response = await fetch(`${API_BASE}/api/profile/picture`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ image: base64String }),
        });

        const data = await response.json();

        if (response.ok && data.ok) {
          setProfilePictureUrl(data.url);
          setStatus({ ok: true, err: '' });
        } else {
          setStatus({ ok: false, err: data.error || 'Failed to upload image' });
        }
        
        setUploadingImage(false);
      };

      reader.onerror = () => {
        setStatus({ ok: false, err: 'Failed to read image file' });
        setUploadingImage(false);
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Image upload error:', error);
      setStatus({ ok: false, err: 'Failed to upload image' });
      setUploadingImage(false);
    }
  }

  function startEdit() {
    setDraft(username || '');
    setEditing(true);
    setStatus({ ok: false, err: '' });
  }

  function cancelEdit() {
    setDraft(username || '');
    setEditing(false);
  }

  async function onSave() {
    setStatus({ ok: false, err: '' });
    const name = (draft || '').trim();
    if (!/^[a-zA-Z0-9_.-]{3,20}$/.test(name)) {
      setStatus({ ok: false, err: "3–20 chars: a–z, 0–9, '.', '-', '_' only" });
      return;
    }
    const res = await updateUsername(name);
    if (!res.success) {
      setStatus({ ok: false, err: res.error || 'Failed to save changes' });
      return;
    }
    setUsername(res.user?.username || name);
    setEditing(false);
    setStatus({ ok: true, err: '' });
  }

  const renderContent = () => {
    switch (activeSection) {
      case 'personal-info':
        return (
          <>
            <Typography variant="h4" gutterBottom>Personal Info</Typography>

            <Box sx={{ display: 'flex', gap: 4, mt: 3, flexWrap: 'wrap' }}>
              {/* Profile Picture Section */}
              <Paper elevation={2} sx={{ p: 3, textAlign: 'center', minWidth: 250 }}>
                <Avatar
                  src={profilePictureUrl || undefined}
                  sx={{
                    width: 150,
                    height: 150,
                    margin: '0 auto',
                    bgcolor: '#667eea',
                    fontSize: '3rem',
                  }}
                >
                  {!profilePictureUrl && <PersonIcon sx={{ fontSize: '5rem' }} />}
                </Avatar>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleImageUpload}
                />
                
                <Button
                  variant="contained"
                  sx={{
                    mt: 2,
                    bgcolor: '#F4E4A0',
                    color: '#000',
                    fontWeight: 'bold',
                    '&:hover': {
                      bgcolor: '#F0DC85',
                    },
                  }}
                  startIcon={uploadingImage ? <CircularProgress size={20} /> : <EditIcon />}
                  disabled={uploadingImage}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploadingImage ? 'UPLOADING...' : 'CHANGE PROFILE PICTURE'}
                </Button>
              </Paper>

              {/* User Information Section */}
              <Box sx={{ flex: 1, minWidth: 300 }}>
                <Stack spacing={2}>
                  {/* Name Field */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        NAME
                      </Typography>
                      <EditIcon sx={{ ml: 'auto', fontSize: 18, color: '#F4E4A0', cursor: 'not-allowed', opacity: 0.5 }} />
                    </Box>
                    <TextField
                      fullWidth
                      value=""
                      placeholder="Enter your name"
                      InputProps={{ readOnly: true }}
                      size="small"
                    />
                  </Box>

                  {/* Username Field */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        USERNAME
                      </Typography>
                      {!editing && (
                        <EditIcon
                          sx={{ ml: 'auto', fontSize: 18, color: '#F4E4A0', cursor: 'pointer' }}
                          onClick={startEdit}
                        />
                      )}
                    </Box>
                    <TextField
                      fullWidth
                      value={editing ? draft : (username || '')}
                      onChange={(e) => setDraft(e.target.value)}
                      InputProps={{ readOnly: !editing }}
                      size="small"
                    />
                    {editing && (
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={onSave}
                          disabled={draft === username}
                        >
                          Save
                        </Button>
                        <Button variant="outlined" size="small" onClick={cancelEdit}>
                          Cancel
                        </Button>
                      </Stack>
                    )}
                  </Box>

                  {/* Language Field */}
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                      LANGUAGE
                    </Typography>
                    <TextField
                      fullWidth
                      value="ENGLISH"
                      InputProps={{ readOnly: true }}
                      size="small"
                    />
                  </Box>
                </Stack>

                {status.err && <Alert severity="error" sx={{ mt: 2 }}>{status.err}</Alert>}
                {status.ok && <Alert severity="success" sx={{ mt: 2 }}>Saved!</Alert>}
              </Box>
            </Box>
          </>
        );

      case 'password':
        return (
          <>
            <Typography variant="h4" gutterBottom>Password Management</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              Password management features coming soon...
            </Typography>
          </>
        );

      case 'payment':
        return (
          <>
            <Typography variant="h4" gutterBottom>Payment & Subscriptions</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              Manage your payment methods and subscription plans here.
            </Typography>
          </>
        );

      case 'guide':
        return (
          <>
            <Typography variant="h4" gutterBottom>User Guide & Training</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              Access tutorials, guides, and training materials.
            </Typography>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: 'calc(100vh - 64px)' }}>
      <SettingsSidebar 
        activeSection={activeSection} 
        onSectionChange={setActiveSection} 
      />
      
      <Box sx={{ flex: 1, p: 3 }}>
        {renderContent()}
      </Box>
    </Box>
  );
}
